import debounce from 'lodash/debounce';
import mean from 'lodash/mean';
import throttle from 'lodash/throttle';

import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { addAnalytics } from '../../analytics/utils';
import { type ProactiveAIConfig } from '../../types';
import { type ParagraphChunk } from '../../utils/diff-match-patch/utils';
import { median } from '../../utils/median';
import { isChunkExcluded } from '../ai-spelling-grammar/api';
import type { ProactiveAIBlock } from '../ai-spelling-grammar/states';
import { getBlocksSentencesNeedingSGCheck } from '../ai-spelling-grammar/utils';

import { createCommand, getPluginState } from './plugin-factory';
import { ACTIONS } from './types';
import { markBlocksNotNeedingSGCheck } from './utils';

export const updatePluginStateWithAnalyticsData = async (
	view: EditorView,
	blocksNeedingSpellAndGrammerCheck: ProactiveAIBlock[],
	locale: string,
) => {
	const pluginState = getPluginState(view.state);
	const {
		proactiveAIBlocks,
		splitParagraphIntoSentences,
		durationBetweenRequests,
		lastRequestTime,
		partLengths,
		partsPerRequests,
		totalRequests,
	} = pluginState;

	const paragraphChunks: ParagraphChunk[] = splitParagraphIntoSentences
		? getBlocksSentencesNeedingSGCheck(blocksNeedingSpellAndGrammerCheck)
		: blocksNeedingSpellAndGrammerCheck;

	const filteredPartLengths = paragraphChunks.filter((chunk) => !isChunkExcluded(chunk));
	const currentPartLengths = filteredPartLengths.map((chunk) => chunk.text.length);

	createCommand(
		() => {
			return {
				type: ACTIONS.UPDATE_PLUGIN_STATE,
				data: {
					totalRequests: totalRequests + 1,
					partLengths: partLengths.concat(currentPartLengths),
					durationBetweenRequests:
						lastRequestTime > 0
							? durationBetweenRequests.concat(...[new Date().getTime() - lastRequestTime])
							: durationBetweenRequests,
					partsPerRequests: partsPerRequests.concat(...[currentPartLengths.length]),
					lastRequestTime: new Date().getTime(),
					proactiveAIBlocks: proactiveAIBlocks
						? markBlocksNotNeedingSGCheck(proactiveAIBlocks, blocksNeedingSpellAndGrammerCheck)
						: proactiveAIBlocks,
				},
			};
		},
		(tr: Transaction) => {
			return tr.setMeta('addToHistory', false);
		},
	)(view.state, view.dispatch);
};

const isSelectionInBlock = (from: number, to: number, block: ProactiveAIBlock) => {
	return (block.from <= from && block.to >= from) || (block.from <= to && block.to >= to);
};

const getBlocksNeedingSpellAndGrammerCheck = (state: EditorState) => {
	const { proactiveAIBlocks } = getPluginState(state);

	if (!proactiveAIBlocks || !proactiveAIBlocks.length) {
		return [];
	}

	return proactiveAIBlocks?.filter((block) => !!block.needSpellingAndGrammarCheck);
};

const getBlocksEligibleForSGPrompt = (state: EditorState, currentBlocks: boolean) => {
	const blocksNeedingSpellAndGrammerCheck = getBlocksNeedingSpellAndGrammerCheck(state);

	const { from, to } = state.selection;
	return blocksNeedingSpellAndGrammerCheck.filter((block) => {
		const selectionInBlock = isSelectionInBlock(from, to, block);
		return currentBlocks ? selectionInBlock : !selectionInBlock;
	});
};
/**
 * Wait Y (Y > X) seconds before triggering S+G prompt for current blocks.
 * That means,
 *  Author starts updating block and keeps updating for sometime.
 *  Then Author pauses for Y (Y > X) seconds then trigger S+G prompt.
 */
export const triggerSpellingAndGrammarCheckForCurrentBlocks = async (
	view: EditorView,
	locale: string,
) => {
	/**
	 * If selection is overlap with block and it's been more than Y seconds
	 *  since it was last updated then send it for S+G prompt.
	 * Ignore non current blocks, as they will be handled separately.
	 */
	const blocksEligibleForSGPrompt = getBlocksEligibleForSGPrompt(view.state, true);

	if (blocksEligibleForSGPrompt.length) {
		await updatePluginStateWithAnalyticsData(view, blocksEligibleForSGPrompt, locale);
	}
};

/**
 * Wait X seconds before triggering S+G prompt for non current blocks.
 * That means,
 *  Author starts updating block, till cursor (or selection) is in the block,
 *    and S+G prompt hasn't been run since last update then,
 *    trigger S+G prompts after X seconds.
 */
export const triggerSpellingAndGrammarCheckForNonCurrentBlocks = async (
	view: EditorView,
	locale: string,
) => {
	const blocksEligibleForSGPrompt = getBlocksEligibleForSGPrompt(view.state, false);

	if (blocksEligibleForSGPrompt.length) {
		await updatePluginStateWithAnalyticsData(view, blocksEligibleForSGPrompt, locale);
	}
};

export const createTriggerSpellingAndGrammerCheck = (timings: ProactiveAIConfig['timings']) => {
	const triggerSpellingAndGrammarCheckForNonCurrentBlocksThrottled = throttle(
		triggerSpellingAndGrammarCheckForNonCurrentBlocks,
		timings.nonCurrentChunks,
	);

	const triggerSpellingAndGrammarCheckForCurrentBlocksDebounced = debounce(
		triggerSpellingAndGrammarCheckForCurrentBlocks,
		timings.currentChunks,
		{ maxWait: timings.currentChunksMaxWait },
	);

	const triggerSpellingAndGrammerCheck = (view: EditorView, locale: string) => {
		triggerSpellingAndGrammarCheckForNonCurrentBlocksThrottled(view, locale);
		triggerSpellingAndGrammarCheckForCurrentBlocksDebounced(view, locale);
	};

	const cancelDebouncedAndThrottledSpellingAndGrammerCheck = () => {
		triggerSpellingAndGrammarCheckForNonCurrentBlocksThrottled.cancel();
		triggerSpellingAndGrammarCheckForCurrentBlocksDebounced.cancel();
	};

	return {
		triggerSpellingAndGrammerCheck,
		cancelDebouncedAndThrottledSpellingAndGrammerCheck,
	};
};

export const sendAnalytics = createCommand(
	() => {
		return {
			type: ACTIONS.UPDATE_PLUGIN_STATE,
			data: {
				totalRequests: 0,
				partLengths: [],
				durationBetweenRequests: [],
				partsPerRequests: [],
				lastRequestTime: 0,
			},
		};
	},
	(tr: Transaction, state: EditorState) => {
		const pluginState = getPluginState(state);
		// Sometimes when plugin is destroyed before it's state is created,
		//	then here we won't have pluginState.
		if (!pluginState) {
			return tr;
		}
		const { totalRequests, partLengths, durationBetweenRequests, partsPerRequests } = pluginState;
		if (totalRequests > 0) {
			const medianRequestFrequency =
				durationBetweenRequests.length > 0 ? median(durationBetweenRequests) : 0;
			const meanRequestFrequency =
				durationBetweenRequests.length > 0 ? mean(durationBetweenRequests) : 0;
			const meanPartsPerRequest = mean(partsPerRequests);
			const maxPartsPerRequest = Math.max(...partsPerRequests);
			return addAnalytics({
				editorState: state,
				tr,
				payload: {
					action: 'trafficProjection',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'spellingSuggestion',
					attributes: {
						totalRequests,
						partLengths,
						medianRequestFrequency,
						meanRequestFrequency,
						meanPartsPerRequest,
						maxPartsPerRequest,
					},
					eventType: EVENT_TYPE.OPERATIONAL,
				},
			});
		}
		return tr;
	},
);
